import React from 'react';
import { Calendar, FileText, Send, Upload, Home, Trophy, CheckCircle2, Clock, Zap, Phone, PhoneCall } from 'lucide-react';
import { MetricTile } from './MetricTile';
import { motion } from 'framer-motion';
import { LoanStage } from '../data/loans';
import { OfficerData } from '../data/officers';
interface TodayScorecardProps {
  officer: OfficerData;
  onTileClick?: (stage: LoanStage) => void;
  onSpeedBonusClick?: () => void;
  onAnswerRateClick?: () => void;
  onDialsClick?: () => void;
}
export function TodayScorecard({
  officer,
  onTileClick,
  onSpeedBonusClick,
  onAnswerRateClick,
  onDialsClick
}: TodayScorecardProps) {
  const {
    todayTarget,
    todayPoints,
    todayCreditPulls,
    todayDocOuts,
    todaySubmissions,
    todayClosings
  } = officer;
  const remaining = Math.max(0, todayTarget - todayPoints);
  const beatGoal = todayPoints >= todayTarget;
  const percentRemaining = todayTarget > 0 ? Math.round(remaining / todayTarget * 100) : 0;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.1
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-963" data-mp="30rttvxbhhum8">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between text-white" data-id="element-964" data-mp="35j3jwz0y2zyn">
        <div className="flex items-center gap-2" data-id="element-965" data-mp="2hszfwqjugy74">
          <Calendar className="w-5 h-5" data-id="element-966" data-mp="2hvvf0i7fhsx5" />
          <h2 className="font-bold tracking-wide" data-id="element-967" data-mp="2cv7nzwj79c2x">TODAY'S SCORECARD</h2>
        </div>
        <span className="text-sm font-medium text-blue-200" data-id="element-968" data-mp="2qu7e78md1z17">May 14, 2025</span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-969" data-mp="2lvckhbmuuq2b">
        {/* Top Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x divide-gray-200 gap-y-4 mb-6" data-id="element-970" data-mp="2lxgywnidr7gu">
          <div className="text-center px-2 min-w-0" data-id="element-971" data-mp="66b8chjqt9jo">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-972" data-mp="15wxysuo5nih">
              Today's Target
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-973" data-mp="2uwtyl8qtsbms">
              {todayTarget}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-974" data-mp="33xs3rud02p6y">
              Points
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-975" data-mp="38x4oo9kb9m6w">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-976" data-mp="38us7hymdnfuw">
              Remaining Today
            </p>
            <p className={`text-3xl font-extrabold leading-none ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-977" data-mp="2g7petn4dtzck">
              {remaining}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-978" data-mp="2pxqsdkz3zzd8">
              Points
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-979" data-mp="1shldjmeo2boq">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-980" data-mp="1xhpt9a7r32dw">
              Days Left
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-981" data-mp="1pygltmit1drh">
              17
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-982" data-mp="1l057ayhkef2x">
              In Month
            </p>
          </div>
          <div className="text-center px-2 min-w-0" data-id="element-983" data-mp="1g23s4on9ezwq">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-984" data-mp="1bz76sq6c4pp3">
              Daily Target
            </p>
            <p className="text-3xl font-extrabold text-[#0a1e5e] leading-none" data-id="element-985" data-mp="170ni4i0cwzt1">
              {todayTarget}
            </p>
            <p className="text-[10px] font-bold text-gray-400 uppercase mt-1" data-id="element-986" data-mp="x9j1ldv196w3">
              Pts / Day
            </p>
          </div>
        </div>

        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-987" data-mp="sbhmt54n3tqf">
          Today's Performance
        </h3>

        {/* Metric Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-id="element-988" data-mp="ja06blnblq17">
          <MetricTile title="Call Answer Rate" points={0} count={officer.todayCallAnswerRate} countSuffix="%" countLabel="Answer Rate" icon={Phone} colorClass="text-teal-600" bgClass="bg-teal-100" hidePoints onClick={onAnswerRateClick} data-id="element-989" data-mp="jbut75i5js7x" />
          <MetricTile title="Dials" points={0} count={officer.todayDials} countLabel="Dials" icon={PhoneCall} colorClass="text-indigo-600" bgClass="bg-indigo-100" hidePoints onClick={onDialsClick} data-id="element-990" data-mp="2ejzvg7nzzioq" />
          <MetricTile title="Credit Pulls" points={750} count={todayCreditPulls} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-991" data-mp="1qowwv7ze60v2" />
          <MetricTile title="Doc Outs" points={500} count={todayDocOuts} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-992" data-mp="3yqwup4org0d" />
          <MetricTile title="Submissions" points={500} count={todaySubmissions} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-993" data-mp="7zj63idk5kwn" />
          <MetricTile title="Closings" points={250} count={todayClosings} countLabel="Closings" icon={Home} colorClass="text-orange-500" bgClass="bg-orange-100" onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-994" data-mp="vnu1w74ecue2" />
        </div>

        {/* Success Banner */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4" data-id="element-995" data-mp="wh4w7gs9gw9h">
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-996" data-mp="11dlj67nq54kz">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${beatGoal ? 'bg-green-600' : 'bg-[#0a1e5e]'}`} data-id="element-997" data-mp="16cgbq1bhu7ia">
              <Trophy className="w-6 h-6 text-white" data-id="element-998" data-mp="1feft4uqf7dlr" />
            </div>
            <div className="min-w-0" data-id="element-999" data-mp="1fc3dio84xfbv">
              <p className="text-xs font-bold text-gray-500 uppercase leading-tight" data-id="element-1000" data-mp="1kcr1t24zbi2e">
                Total Points Today
              </p>
              <div className="flex items-baseline gap-1 flex-wrap" data-id="element-1001" data-mp="1p4hhlnmoqy17">
                <span className={`text-3xl font-extrabold leading-none ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-1002" data-mp="1u3c16rsh8qmd">
                  {todayPoints.toLocaleString()}
                </span>
                <span className="text-xs font-bold text-gray-500" data-id="element-1003" data-mp="234a2v3fg0m1s">POINTS</span>
              </div>
            </div>
          </div>
          {beatGoal ? <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-1004" data-mp="3jjoatqdq9i9k">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center shrink-0" data-id="element-1005" data-mp="3ekbhqnw9jeyj">
                <CheckCircle2 className="w-6 h-6 text-white" data-id="element-1006" data-mp="39nbjv09khiii" />
              </div>
              <p className="text-sm font-bold text-green-700 uppercase leading-tight" data-id="element-1007" data-mp="b4nv7vvoc22q">
                You Beat Today's Goal!
              </p>
            </div> : <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3 min-w-0" data-id="element-1008" data-mp="1ebb24rruax4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0" data-id="element-1009" data-mp="3sle4z3yzuvb1">
                <Zap className="w-6 h-6 text-white" data-id="element-1010" data-mp="tv5hbhm03zf4" />
              </div>
              <p className="text-sm font-bold text-blue-700 uppercase leading-tight" data-id="element-1011" data-mp="p4ff2d8vgcon">
                {remaining} pts to hit today's goal
              </p>
            </div>}
        </div>

        {/* Speed Bonus */}
        {(() => {
        const Wrapper: any = onSpeedBonusClick ? 'button' : 'div';
        return <Wrapper {...onSpeedBonusClick ? {
          type: 'button',
          onClick: onSpeedBonusClick,
          'aria-label': 'View speed bonus loans for today'
        } : {}} className={`group relative w-full bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3 mb-6 transition-all text-left ${onSpeedBonusClick ? 'hover:border-orange-400 hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400' : ''}`} data-id="element-1012" data-mp="3kmbojjypq1ly">
              <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-1013" data-mp="2rvbil3gtmnrn">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-1014" data-mp="2wwr38uf36s9i">
                  <Clock className="w-5 h-5 text-white" data-id="element-1015" data-mp="2od4b5ytm9f89" />
                </div>
                <div className="min-w-0" data-id="element-1016" data-mp="2kc29lbc88r34">
                  <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-1017" data-mp="2kf6k0qgacj28">
                    Speed Bonus
                  </p>
                  <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-1018" data-mp="2tdil5mes2z8x">
                    Close a loan within 14 days of credit pull
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-1019" data-mp="20pw6nj40ia7a">
                <div className="flex items-baseline gap-1 justify-end" data-id="element-1020" data-mp="20n1ojy8h11h1">
                  <span className="text-2xl font-extrabold text-orange-500 leading-none" data-id="element-1021" data-mp="2ffllui8xweqk">
                    0
                  </span>
                  <span className="text-xs font-bold text-gray-500" data-id="element-1022" data-mp="2alhgu4j8igs4">PTS</span>
                </div>
                <p className="text-[10px] font-bold text-gray-500 uppercase mt-1" data-id="element-1023" data-mp="1mvdee0hs8n2p">
                  Today
                </p>
              </div>
              {onSpeedBonusClick && <span className="absolute top-2 right-2 text-[10px] font-bold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider" data-id="element-1024" data-mp="325i2mdizj4kb">
                  View loans →
                </span>}
            </Wrapper>;
      })()}

        {/* Footer Stats */}
        <div className="mt-auto grid grid-cols-2 divide-x divide-gray-200 pt-4 border-t border-gray-100" data-id="element-1025" data-mp="2j8qbxj145n68">
          <div className="text-center" data-id="element-1026" data-mp="2ei9zo29147lg">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1" data-id="element-1027" data-mp="2eh8c4yattsmu">
              Goal Remaining Today
            </p>
            <div className="flex items-baseline justify-center gap-1" data-id="element-1028" data-mp="29gt1wflud193">
              <span className={`text-2xl font-extrabold ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-1029" data-mp="24l58fo0btlxs">
                {remaining}
              </span>
              <span className="text-xs font-bold text-gray-400" data-id="element-1030" data-mp="1vk75za5jdmhb">POINTS</span>
            </div>
          </div>
          <div className="text-center" data-id="element-1031" data-mp="b0brvqttebh8">
            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1" data-id="element-1032" data-mp="b3g67h0zunlm">
              % Of Goal Remaining
            </p>
            <span className={`text-2xl font-extrabold ${beatGoal ? 'text-green-600' : 'text-[#0a1e5e]'}`} data-id="element-1033" data-mp="k19v46wfieyr">
              {percentRemaining}%
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}